// client/src/components/merchant/EmailVerificationBanner.js
// Rappel PERMANENT tant que l'email du marchand n'est pas verifie.
// Monte dans MerchantLayout : le rappel apparait donc sur CHAQUE page du
// back-office marchand, pas seulement sur le tableau de bord.
//
// Pourquoi ce rappel est bloquant a afficher : sans email verifie,
// `canDealGoLive` cote serveur refuse la publication. Le marchand cree des
// annonces qui restent en attente sans jamais comprendre pourquoi, puis
// ouvre un ticket au support.
//
// Non refermable volontairement : c'est un blocage, pas une suggestion.
// FR / AR (Cairo pour le bloc arabe), react-icons, aucun emoji.
// Maxula

import React, { useEffect, useState } from 'react';
import { FiMail, FiRefreshCw, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';

const STR = {
  fr: {
    title: 'Vérifiez votre adresse email',
    body: (email) =>
      `Un lien de confirmation a été envoyé à ${email}. Tant que votre email n'est pas vérifié, vos annonces ne peuvent pas être mises en ligne.`,
    hint: "Pensez à regarder dans les courriers indésirables (spam).",
    resend: "Renvoyer l'email",
    sending: 'Envoi en cours…',
    sent: 'Email de vérification renvoyé. Vérifiez votre boîte mail.',
    wait: 'Patientez quelques minutes avant un nouvel envoi.',
    check: "J'ai déjà vérifié",
    checking: 'Vérification…',
    okNow: 'Email confirmé. Merci !',
    stillNo: "L'email n'est pas encore confirmé. Cliquez sur le lien reçu par email.",
    checkFailed: 'Impossible de vérifier pour le moment.'
  },
  ar: {
    title: 'يرجى تأكيد بريدكم الإلكتروني',
    body: (email) =>
      `تم إرسال رابط التأكيد إلى ${email}. لن يتم نشر عروضكم قبل تأكيد البريد الإلكتروني.`,
    hint: 'تحقّقوا أيضاً من مجلد الرسائل غير المرغوب فيها.',
    resend: 'إعادة إرسال الرسالة',
    sending: 'جارٍ الإرسال…',
    sent: 'تمت إعادة إرسال رسالة التأكيد.',
    wait: 'انتظروا بضع دقائق قبل إرسال رسالة جديدة.',
    check: 'لقد أكّدت بريدي',
    checking: 'جارٍ التحقق…',
    okNow: 'تم تأكيد البريد الإلكتروني. شكراً لكم!',
    stillNo: 'لم يتم تأكيد البريد بعد. يرجى الضغط على الرابط المرسل إليكم.',
    checkFailed: 'تعذّر التحقق حالياً.'
  }
};

// Le serveur limite a 3 renvois par heure ; on bloque le bouton 2 min cote
// client pour eviter que le marchand brule son quota en cliquant 3 fois.
const COOLDOWN_MS = 2 * 60 * 1000;

const EmailVerificationBanner = () => {
  const { user, refreshUser } = useAuth();
  const { lang, isRTL } = useLanguage();
  const L = STR[lang === 'ar' ? 'ar' : 'fr'];

  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, forceTick] = useState(0);

  const emailVerified = !!user?.emailVerified;

  // ATTENTION — ne JAMAIS rappeler refreshUser() automatiquement ici.
  // Ce composant est monte dans MerchantLayout ; MerchantRoute remplace tout
  // l'arbre par un spinner des que l'etat `loading` d'AuthContext passe a true.
  // Un refresh automatique se demontait donc lui-meme, se remontait, se
  // relancait... la page clignotait en boucle puis la session sautait.
  // La verification est desormais declenchee par le marchand, via le bouton
  // "J'ai deja verifie", et passe par un appel silencieux.

  // Rafraichit l'affichage du compte a rebours du bouton.
  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);

  if (!user || emailVerified) return null;
  // Rappel reserve aux comptes marchands (les clients ont leur propre parcours).
  if (user.role !== 'merchant' && !user.isTeamMember) return null;

  const remainingMs = Math.max(0, cooldownUntil - Date.now());
  const locked = sending || remainingMs > 0;

  const resend = async () => {
    if (locked) return;
    setSending(true);
    try {
      await authAPI.resendVerification();
      toast.success(L.sent);
      setCooldownUntil(Date.now() + COOLDOWN_MS);
    } catch (e) {
      toast.error(e?.response?.data?.message || L.wait);
    } finally {
      setSending(false);
    }
  };

  // Verification a la demande : on interroge d'abord l'API sans passer par le
  // contexte (donc sans toucher a `loading`, donc sans demonter la page). On ne
  // rafraichit la session QUE si l'email est effectivement confirme -- le
  // bandeau disparait alors, il n'y a pas de remontage en boucle possible.
  const checkNow = async () => {
    if (checking) return;
    setChecking(true);
    try {
      const res = await authAPI.getMe();
      const payload = res?.data || {};
      const u = payload.user || payload.data?.user || payload.data || payload;
      if (u && u.emailVerified) {
        if (typeof refreshUser === 'function') await refreshUser({ silent: true });
        toast.success(L.okNow);
      } else {
        toast.info(L.stillNo);
      }
    } catch (e) {
      toast.error(L.checkFailed);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="m-4 mb-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={isRTL ? { fontFamily: "'Cairo', Arial, sans-serif" } : undefined}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <FiAlertTriangle className="shrink-0 mt-0.5 text-amber-600" size={20} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-amber-900 flex items-center gap-2">
            <FiMail size={16} /> {L.title}
          </p>
          <p className="text-sm text-amber-900 mt-1">{L.body(user.email || '')}</p>
          <p className="text-xs text-amber-700 mt-1">{L.hint}</p>
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={locked}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
          style={isRTL ? { fontFamily: "'Cairo', Arial, sans-serif" } : undefined}
        >
          <FiRefreshCw size={15} className={sending ? 'animate-spin' : ''} />
          {sending
            ? L.sending
            : remainingMs > 0
            ? `${Math.ceil(remainingMs / 1000)}s`
            : L.resend}
        </button>

        <button
          type="button"
          onClick={checkNow}
          disabled={checking}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-amber-600 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
          style={isRTL ? { fontFamily: "'Cairo', Arial, sans-serif" } : undefined}
        >
          <FiCheckCircle size={15} />
          {checking ? L.checking : L.check}
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
